import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-course-card.css.lit';
import { IdbContentCard } from '../idb-content-card/idb-content-card';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';
import { LinkTargetType } from '../../base/outline-link/config';
import { getUrlTarget } from '../../../utilities/url_utils';

/**
 * The IDB Course Card component.
 * @element idb-course-card
 * @extends IDBContentCard
 * @see Events Card for similar styling
 */
@customElement('idb-course-card')
export class CourseCard extends IdbContentCard {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, reflect: true, attribute: 'url' })
  url: string;

  @property({ type: Boolean, reflect: true, attribute: 'truncate-title' })
  truncateTitle: false;

  @property({ type: String, reflect: true, attribute: 'link-target' })
  linkTarget: LinkTargetType;

  @property({ type: String, reflect: false, attribute: 'title-space' })
  titleSpace: 'normal';

  render(): TemplateResult {
    const cardClasses = {
      'card': true,
      'truncate-title': this.truncateTitle,
      [`image-position--${this.imagePosition}`]: this.imagePosition,
      'outline-shadow': this.hasShadow,
      'outline-rounded-2xl': this.isRounded,
      [`outline-bg-${this.cardBackground}`]: this.cardBackground !== undefined,
      [`content-mode--${this.contentMode}`]: this.contentMode,
    };
    const imageClasses = {
      'card-image': true,
    };
    const contentWrapperClasses = {
      'card-content-wrapper': true,
      [`content-alignment--${this.contentAlignment}`]: this.contentAlignment,
    };
    const eyebrowClasses = {
      'card-eyebrow': true,
      [`eyebrow-alignment--${this.eyebrowAlignment}`]: this.eyebrowAlignment,
    };
    const titleClasses = {
      'card-title': true,
      [`title-alignment--${this.titleAlignment}`]: this.titleAlignment,
      [`title-mode--${this.titleMode}`]: this.titleMode,
      [`${this.titleSpace}`]: true,
    };
    const summaryClasses = {
      'card-summary': true,
    };
    const contentClasses = {
      'card-content': true,
    };
    const ctaClasses = {
      'card-cta': true,
      [`cta-alignment--${this.ctaAlignment}`]: this.ctaAlignment,
    };

    if (this.url) {
      this.linkTarget = getUrlTarget(this.url, this.linkTarget);
      return html`
        <a href="${this.url}" target="${this.linkTarget}">
          ${this.renderContent(
            cardClasses,
            imageClasses,
            contentWrapperClasses,
            eyebrowClasses,
            titleClasses,
            summaryClasses,
            contentClasses,
            ctaClasses
          )}
        </a>
      `;
    } else {
      return this.renderContent(
        cardClasses,
        imageClasses,
        contentWrapperClasses,
        eyebrowClasses,
        titleClasses,
        summaryClasses,
        contentClasses,
        ctaClasses
      );
    }
  }

  renderContent(
    cardClasses: ClassInfo,
    imageClasses: ClassInfo,
    contentWrapperClasses: ClassInfo,
    eyebrowClasses: ClassInfo,
    titleClasses: ClassInfo,
    summaryClasses: ClassInfo,
    contentClasses: ClassInfo,
    ctaClasses: ClassInfo
  ) {
    return html`
      <section class="${classMap(cardClasses)}">
        ${this.imageTemplate(imageClasses)}
        <section class="${classMap(contentWrapperClasses)}">
          ${this.eyebrowTemplate(eyebrowClasses)}
          ${this.titleTemplate(titleClasses)}
          ${this.summaryTemplate(summaryClasses)}
          ${this.contentTemplate(contentClasses)}
          ${this.ctaTemplate(ctaClasses)}
        </section>
      </section>
    `;
  }

  imageTemplate(classes: ClassInfo): TemplateResult | null {
    return html`
      <section class="${classMap(classes)}">
      <div class="image-wrapper">
        <div class="image-tag-wrapper">
        ${this.slotsFancy.renderInShadow('tags')} 
        </div>
        ${this.slotsFancy.renderInShadow('image')}  
     </div>
     </section>`;
  }

  /**
   * Template partial for the Title slot.
   * @param classes
   * @returns TemplateResult | null
   */
  titleTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasTitleSlot) return null;

    return html` <section class="${classMap(classes)}">
        <div class="title">
          ${this.slotsFancy.renderInShadow('title')}
        </div>
      </section>`;
  }
}
